/* eslint-disable @typescript-eslint/naming-convention */
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Pagination } from './pagination.model';
import { CustomToastrService } from '../../../../services/custom-toastr.service';

/**
 * Pagination Component
 * Following Single Responsibility Principle: Manages pagination UI state and user interactions
 * Following Dependency Inversion Principle: Services implement abstractions for page calculation
 *
 * Responsibility: Display pagination controls, handle user navigation, and emit page change events
 *
 * Features:
 * - Desktop: Right-aligned pagination with minimal page numbers
 * - Mobile: Center-aligned pagination with more page numbers
 * - Active page always appears first (leftmost)
 * - Clickable ellipsis for quick navigation (jumps 3 pages forward/backward)
 * - Previous/Next navigation with proper disabled states
 *
 * Inputs:
 * - pagination: Pagination model with page state
 *
 * SSR Compatibility: All calculations are SSR-safe, no browser-specific APIs required
 */
@Component({
    selector: 'app-pagination',
    templateUrl: './pagination.component.html',
    styleUrl: './pagination.component.scss',
})
export class PaginationComponent implements OnInit, OnChanges {
    @Input() pagination!: Pagination;
    @Output() pageChange = new EventEmitter<number>();
    @Output() elementCountPerPageChanged = new EventEmitter<number>();
    @Output() infiniteScrollTriggered = new EventEmitter<void>();
    @Input() pagePerEllipsisStep: number = 3;
    @Input() show1stPageOnLeftEllipsis: boolean = false;
    /** Translation key of the "Showing X–Y of Z" line; override to name what is being paged (e.g. "questions"). */
    @Input() detailsKey: string = 'pagination.details';

    // Total pages calculation
    totalPages: number = 0;
    currentStep: number = 0;

    // Current page item count for "Showing X to Y of Z" display
    currentPageTotal: number = 0;

    // Page display configuration
    pageNumbersToDisplay: number[] = [];

    // Items per page options
    protected itemsPerPageOptions = [
        { value: 10, label: '10' },
        { value: 20, label: '20' },
        { value: 30, label: '30' },
        { value: 50, label: '50' },
        // "All" represents cap at 100 items with infinite vertical scroll.
        { value: -1, label: 'All' }
    ];

    protected selectedItemsPerPage: number = 10;

    constructor(private customToastrService: CustomToastrService) { }

    ngOnInit(): void {
        const initOption = this.itemsPerPageOptions.find(
            option => option.value == this.pagination.elementCountPerPage
        );
        this.selectedItemsPerPage = initOption ? initOption.value : -1;
        
        this.initializePagination();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Recalculate when pagination input changes
        if (changes['pagination']) {
            this.selectedItemsPerPage = this.pagination.elementCountPerPage;
            this.initializePagination();
        }
    }

    /**
     * Initialize pagination calculations
     * SSR-safe: Pure calculations, no browser APIs
     */
    private initializePagination(): void {
        this.calculateTotalPages();
        this.calculateCurrentPageTotal();
        this.updatePageDisplay();
    }

    /**
     * Calculate total number of pages
     */
    private calculateTotalPages(): void {
        this.totalPages = Math.ceil(this.pagination.total / this.pagination.elementCountPerPage);
    }

    /**
     * Calculate how many items are on the current page for display text
     */
    private calculateCurrentPageTotal(): void {
        const pageEndIndex = this.pagination.currentPage * this.pagination.elementCountPerPage;

        if (pageEndIndex <= this.pagination.total) {
            this.currentPageTotal = this.pagination.elementCountPerPage;
        }
        else {
            // Last page may have fewer items
            this.currentPageTotal = Math.abs(
                this.pagination.total - (this.pagination.currentPage - 1) * this.pagination.elementCountPerPage
            );
        }
    }

    /**
     * Update which pages to display using the calculator service
     */
    private updatePageDisplay(): void {
        this.pageNumbersToDisplay = this.calculateDisplayPages();
    }

    private calculateDisplayPages(): number[] {
        let startPage = 0;
        let endPage = 0;
    
        if (this.shouldShowRightEllipsis()) {
            // Mathematic Formula:
            // E.g.
            // Current Page = 4
            // I) Page Per Ellipsis Step = 3 (1 "..." click, 4 -> 7)
            // II) Start Page
            // = (ceil(4/3) * 3) - 3 + 1
            // = (1 * 3) - 3 + 1
            // = 3 - 3 + 1
            // = 1 (Page Set #1)
            startPage = 
                (Math.ceil(this.pagination.currentPage/this.pagePerEllipsisStep) * this.pagePerEllipsisStep) - 
                this.pagePerEllipsisStep + 1;
            
            // End Page
            // E.g.
            // I) Current Page = 4
            // II) Page Per Ellipsis Step = 3 (1 "..." click, 4 -> 7)
            // = 1 + (3 - 1)
            // = 1 + 2
            // = 3
            //
            // End-Result:
            // < 1 2 3 ... (Last Page) >
            endPage = startPage + (this.pagePerEllipsisStep - 1);
        }
        else if (this.shouldShowLeftEllipsis()) {
            startPage = Math.max(
                this.totalPages - this.pagePerEllipsisStep,
                this.pagePerEllipsisStep + 1
            );
            endPage = this.totalPages;
        }
        // No ellipsis: show all pages
        else {
            startPage = 1;
            endPage = this.totalPages;
        }
        let pageNumbers = [];

        for (let i = Math.min(startPage, endPage); i <= Math.max(startPage, endPage); ++i) {
            pageNumbers.push(i);
        }
        return pageNumbers;
    }

    /**
     * Check if left ellipsis should be displayed
     * Left ellipsis appears before the page number range when current page > 4
     */
    protected shouldShowLeftEllipsis(): boolean {
        const doesMeetTotalPageCountToShowEllipsisCriterion = 
            this.totalPages > (this.pagePerEllipsisStep * 2 - 1);
            
        const doesMeetOffsetFromLastPageToShowEllipsisCriterion = 
            (this.totalPages - this.pagination.currentPage) <= this.pagePerEllipsisStep;

        const isOverMinCurrentPageToShowEllipsisCriterion = this.pagination.currentPage > this.pagePerEllipsisStep;
            
        return doesMeetTotalPageCountToShowEllipsisCriterion && 
            doesMeetOffsetFromLastPageToShowEllipsisCriterion &&
            isOverMinCurrentPageToShowEllipsisCriterion;
    }

    /**
     * Check if right ellipsis should be displayed
     * Right ellipsis appears after the page number range when current page < (totalPages - 3)
     */
    protected shouldShowRightEllipsis(): boolean {
        const doesMeetTotalPageCountToShowEllipsisCriterion = 
            this.totalPages > (this.pagePerEllipsisStep * 2 - 1);

        // Scenario #1:
        // When there's 6 total pages
        // < 1 2 [3] ... >
        // < ... [4] 5 6 >

        // Scenario #2:
        // When there's >6 total pages
        // < 1 2 [3] ... 7 >
        // < ... [4] 5 6 7 >
        const currentPageMeetShowEllipsisCriterion = 
            this.totalPages > (this.pagePerEllipsisStep * 2) ?  
            (this.totalPages - this.pagination.currentPage) > this.pagePerEllipsisStep : 
            (this.totalPages - this.pagination.currentPage) >= this.pagePerEllipsisStep;

        return doesMeetTotalPageCountToShowEllipsisCriterion && 
            currentPageMeetShowEllipsisCriterion;
    }

    /**
     * Navigate to previous page
     */
    protected prevPage(): void {
        if (this.pagination.currentPage > 1) {
            this.goToPage(this.pagination.currentPage - 1);
        }
    }

    /**
     * Navigate to next page
     */
    protected nextPage(): void {
        if (this.pagination.currentPage < this.totalPages) {
            this.goToPage(this.pagination.currentPage + 1);
        }
    }

    /**
     * Navigate to a specific page
     * @param page - Target page number (1-indexed)
     */
    protected goToPage(page: number): void {
        // Validate page is within bounds
        if (page < 1 || page > this.totalPages) {
            return;
        }

        // Update pagination state
        this.pagination.currentPage = page;

        // Emit page change event to parent
        this.pageChange.emit(page);

        // Recalculate display
        this.calculateCurrentPageTotal();
        this.updatePageDisplay();
    }

    /**
     * Check if current page is the active page
     * @param page - Page number to check
     */
    protected isActivePage(page: number): boolean {
        return this.pagination.currentPage === page;
    }

    /**
     * Check if previous button should be disabled
     */
    protected isPrevDisabled(): boolean {
        return this.pagination.currentPage === 1;
    }

    /**
     * Check if next button should be disabled
     */
    protected isNextDisabled(): boolean {
        return this.pagination.currentPage >= this.totalPages;
    }

    /**
     * Handle ellipsis click to jump pages
     * SSR-safe: Pure calculation, no browser APIs
     * @param direction - 'left' jumps backward 3 pages, 'right' jumps forward 3 pages
     */
    protected onEllipsisClick(direction: 'left' | 'right'): void {
        let targetPage: number;

        // Left ellipsis clicked - jump backward "x" number of pages
        // Right ellipsis clicked - jump forward "x" number of pages
        targetPage = direction === 'left' ? this.pagination.currentPage - this.pagePerEllipsisStep : 
            this.pagination.currentPage +  this.pagePerEllipsisStep;

        // Clamp target page to valid range [1, totalPages]
        targetPage = Math.max(1, Math.min(targetPage, this.totalPages));

        // Navigate to the calculated page
        this.goToPage(targetPage);
    }

    /**
     * Handle items per page selection change
     * @param newElementCountPerPage - New number of elements per page that's actively selected (or -1 for "All")
     */
    protected onItemsPerPageChange(newElementCountPerPage: number): void {
        let value = typeof(newElementCountPerPage) == 'number' ? newElementCountPerPage : parseInt(newElementCountPerPage);
        if (isNaN(value)) {
            this.customToastrService.showError('Invalid Page Size Number');
        }

        // "All" selected - enable infinite scroll mode -> Value = -1
        // Otherwise, Standard pagination mode
        this.pagination.enableInfiniteScroll = value === -1;
        this.pagination.elementCountPerPage = value;

        // Reset to first page
        this.pagination.currentPage = 1;

        // Emit page size change to parent
        this.elementCountPerPageChanged.emit(value);

        // Recalculate display
        this.initializePagination();
    }

    /**
     * Check if infinite scroll mode is enabled
     */
    protected isInfiniteScrollMode(): boolean {
        return this.pagination.enableInfiniteScroll === true || this.selectedItemsPerPage === -1;
    }

    /**
     * Handle infinite scroll event
     * Emits event to parent to load more data
     */
    protected onScroll(): void {
        if (this.isInfiniteScrollMode() && !this.pagination.loading) {
            this.infiniteScrollTriggered.emit();
        }
    }
}
